import {
  Box,
  Flex,
  IconButton,
  Input,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as lodash from 'lodash';
import React, { useCallback, useState } from 'react';

import { validate as athenaValidate } from '@finley-technologies/athena-utilities';
import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  DataLake,
  Util,
} from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { ReportingOutputDocxColumnConfigContainer } from '../docxOutput/ReportingOutputDocxColumnConfigContainer';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useGetAthenaQueryResponseByRawLazyQueryGQLType } from '~/packages/athena/requests/requests.gql.generated';
import { AthenaDataPreviewModal } from '~/packages/athena/ui/AthenaDataPreviewModal';
import { AthenaQueryExecuteModal } from '~/packages/athena/ui/AthenaQueryExecuteModal';
import { AthenaQueryStringWithSchemaSidebarContainer } from '~/packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import { BindingContainer } from '~/packages/athena/ui/BindingContainer';
import { ColumnNamesContainer } from '~/packages/athena/ui/ColumnNamesContainer';
import { inputWidth } from '~/packages/athena/ui/consts';
import { VariableContainer } from '~/packages/athena/ui/VariableContainer';
import {
  AddIconButton,
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  SearchIconButton,
} from '~/packages/common-frontend/components/IconButton';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { DeleteConfirmationModal } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { CreditAgreementFullArtifactWipConfigType } from '~/packages/creditAgreements/ui/types';

type FileInsertAthenaQuery = CreditAgreement.Config.Reporting.Inputs.FileInsertAthenaQuery;

export const createFileInsertAthenaQueryShell = (
  idx: number,
  name?: string,
): FileInsertAthenaQuery => ({
  query: {
    name: name ?? `fileInsertQuery${idx}`,
    queryString: 'SQL QUERY GOES HERE;',
  },
  variables: [],
  bindings: [],
  columnNames: ['placeholderColumn'],
  columns: [],
});

type FileInsertAthenaQueryViewProps = {
  organizationId: string;
  queryConfig: FileInsertAthenaQuery;
  existingQueries: FileInsertAthenaQuery[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  basePropertyKey: string;
  idx: number;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
};

export const FileInsertAthenaQueryView = ({
  organizationId,
  queryConfig,
  existingQueries,
  wipConfig,
  fullPropertyKey,
  basePropertyKey,
  idx,
  presetVariables,
  validationVariables,
}: FileInsertAthenaQueryViewProps) => {
  const toast = useToast();
  const {
    onOpen: onDuplicateQueryOpen,
    isOpen: isDuplicateQueryOpen,
    onClose: onDuplicateQueryClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();
  const { onOpen: onPreviewQueryResultsDrawerOpen } = useDisclosure();
  const queryStringEditModeDisclosure = useDisclosure();

  const inputDateType = 'reportingDate';

  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  const [isExecuteModalOpen, setIsExecuteModalOpen] = useState<boolean>(false);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);
  const [
    isQueryStringSaveLoading,
    setIsQueryStringSaveLoading,
  ] = useState<boolean>(false);

  const [queryStringState, setQueryStringState] = useState<string>(
    queryConfig.query.queryString,
  );
  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[]
  >([]);

  const duplicateQuery = useCallback(
    async ({ queryKeyInput }: { queryKeyInput: string }): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      const wipQueries = getIn(wipConfig.formProps.values, basePropertyKey);
      finleyAssert(
        Array.isArray(wipQueries),
        `Retrieved non-array structure for [key:${basePropertyKey}]`,
      );

      const copiedQuery = lodash.cloneDeep(queryConfig);
      copiedQuery.query.name = queryKeyInput;

      const wipQueriesCopy = lodash.cloneDeep(wipQueries);
      wipQueriesCopy.splice(idx + 1, 0, copiedQuery);

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: basePropertyKey,
        updatedProperty: wipQueriesCopy,
        setIsSaveLoading: setIsDuplicateLoading,
      });
    },
    [basePropertyKey, idx, queryConfig, wipConfig],
  );

  const deleteQuery = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const queries = getIn(wipConfig.formProps.values, basePropertyKey);
    finleyAssert(
      Array.isArray(queries),
      `Retrieved non-array structure for [key:${basePropertyKey}]`,
    );

    const queriesAfterDelete = queries.filter(
      (_unused: FileInsertAthenaQuery, varIdx: number) => varIdx !== idx,
    );

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: queriesAfterDelete,
      setIsSaveLoading: setIsDeleteLoading,
    });
  }, [wipConfig, basePropertyKey, idx]);

  const [isNameEditOpen, setNameEditOpen] = useState<boolean>(false);
  const [name, setName] = useState<string>(queryConfig.query.name);

  const changeName = useCallback((): void => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const nameExists = existingQueries.some((q) => q.query.name === name);
    if (nameExists) {
      toast({
        ...errorToastConfig,
        title: 'Updating Query Name Error',
        description: `"${name}" name is already in use`,
      });
      return;
    }

    const qv = lodash.cloneDeep(
      getIn(wipConfig.formProps.values, fullPropertyKey),
    );
    qv.query.name = name;
    wipConfig.formProps.setFieldValue(fullPropertyKey, qv);

    setNameEditOpen(false);
  }, [existingQueries, fullPropertyKey, name, toast, wipConfig]);

  const [
    executeAthenaQueryByRaw,
    { loading: isQueryLoading, variables: queryVariables },
  ] = useGetAthenaQueryResponseByRawLazyQueryGQLType({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: async (data) => {
      const { Items } = data.athenaQueryResponse;

      if (Items.length === 0 || Object.keys(Items[0]).length === 0) {
        toast({
          title: 'Athena Query Execute Update',
          description:
            'Query returned 0 data points. Query SQL and available column names have not been updated. Choose a different Reporting Date where data exists for the query and try again.',
          status: 'info',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });

        setIsQueryStringSaveLoading(false);
        return;
      }

      if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
        const queryCopy = lodash.cloneDeep(queryConfig);

        queryCopy.columnNames = Object.keys(Items[0]);
        if (queryVariables) {
          if (queryCopy.query.queryString !== queryVariables.query) {
            queryCopy.query.queryString = queryVariables.query;

            await wipConfig?.saveAndUpdateConfigCreditAgreementWip({
              wipConfig,
              propertyKey: fullPropertyKey,
              updatedProperty: queryCopy,
              setIsSaveLoading: setIsQueryStringSaveLoading,
            });
          }
        }

        setIsQueryStringSaveLoading(false);
        setQueryDataItems(Items);
        onPreviewQueryResultsDrawerOpen();
      }
    },
    onError: (error) => {
      setIsQueryStringSaveLoading(false);

      const errorDescription = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Incurred server error when executing Athena query'
        : error.message;
      toast({
        ...errorToastConfig,
        title: 'Athena Query Execute Error',
        description: errorDescription,
      });
    },
  });

  const changeQueryString = useCallback(
    async ({
      newQueryString,
      queryInputDateForColumnUpdate,
    }: {
      newQueryString: string;
      queryInputDateForColumnUpdate: Util.FullDateStr;
    }): Promise<void> => {
      setIsQueryStringSaveLoading(true);

      if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
        return;
      }

      const qv = lodash.cloneDeep(
        getIn(wipConfig.formProps.values, fullPropertyKey),
      );
      qv.query.queryString = newQueryString;

      const errors = athenaValidate.validateAthenaQuery({
        query: qv.query.queryString,
        bindings: qv.bindings,
        variables: [
          ...validationVariables,
          ...presetVariables,
          ...qv.variables,
        ],
      });

      if (errors.length) {
        toast({
          ...errorToastConfig,
          title: 'Updating SQL Query Error',
          description: errors.join(', '),
        });
        setIsQueryStringSaveLoading(false);
        return;
      }

      const { variables, bindings } = queryConfig;

      const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
        {
          name: inputDateType,
          value: queryInputDateForColumnUpdate,
          parseNumber: true,
        },
      ];

      const updatedPresetVariables = presetVariables.filter((preset) => {
        return preset.name !== inputDateType;
      });

      executeAthenaQueryByRaw({
        variables: {
          organizationId,
          variables: [
            ...supplementalVariables,
            ...updatedPresetVariables,
            ...variables,
          ],
          bindings,
          query: newQueryString,
        },
      });
    },
    [
      organizationId,
      wipConfig,
      presetVariables,
      fullPropertyKey,
      toast,
      validationVariables,
      executeAthenaQueryByRaw,
      queryConfig,
    ],
  );

  return (
    <Flex mb="2" p="3" flexDir="column" border="1px" borderColor="gray.700">
      <Flex alignItems="center" mb={isQueryOpen ? '2' : undefined}>
        <Text fontWeight="bold" mr="2">
          Query: &quot;{queryConfig.query.name}&quot;
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <SearchIconButton
              ariaLabel="execute query"
              label="Execute Query"
              onClick={() => setIsExecuteModalOpen(true)}
              isLoading={isQueryLoading}
            />
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <IconButton
              aria-label={`${isQueryOpen ? 'close' : 'display'} config`}
              icon={isQueryOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setIsQueryOpen((prev) => !prev)}
              variant="ghost"
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="singleQuery"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>

      {isQueryOpen ? (
        <Box>
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="4"
          >
            <Text width="40">Query Name:</Text>
            {isNameEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <>
                <Input
                  type="text"
                  width={inputWidth}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <SaveIconButton
                  ariaLabel="save name"
                  label="Save Name"
                  onClick={() => changeName()}
                />
                <EditIconButton
                  ariaLabel="close edit"
                  label="Close Edit"
                  onClick={() => {
                    setName(queryConfig.query.name);
                    setNameEditOpen(false);
                  }}
                />
              </>
            ) : (
              <>
                <Text width={inputWidth}>{queryConfig.query.name}</Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <EditIconButton
                    ariaLabel="edit name"
                    label="Edit Name"
                    onClick={() => setNameEditOpen(true)}
                    h="6"
                  />
                )}
              </>
            )}
          </Flex>

          <VariableContainer
            wipConfig={wipConfig}
            variables={queryConfig.variables}
            variablePropertyKey={`${fullPropertyKey}.variables`}
          />

          <BindingContainer
            wipConfig={wipConfig}
            bindings={queryConfig.bindings}
            bindingsPropertyKey={`${fullPropertyKey}.bindings`}
            presetVariables={presetVariables}
            validationVariables={validationVariables}
            query={queryConfig}
            queryType="singleQuery"
          />

          <AthenaQueryStringWithSchemaSidebarContainer
            wipConfig={wipConfig}
            fullPropertyKey={`${fullPropertyKey}.queryString`}
            changeQueryString={changeQueryString}
            isQueryStringSaveLoading={isQueryStringSaveLoading}
            queryStringState={queryStringState}
            setQueryStringState={setQueryStringState}
            initialQueryString={queryConfig.query.queryString}
            editModeDisclosure={queryStringEditModeDisclosure}
            organizationId={organizationId}
          />

          <ColumnNamesContainer
            wipConfig={wipConfig}
            columnNames={queryConfig.columnNames}
            configPropertyKey={fullPropertyKey}
            executeQueryMethod={() => setIsExecuteModalOpen(true)}
          />

          <ReportingOutputDocxColumnConfigContainer
            columnConfigs={queryConfig.columns}
            wipConfig={wipConfig}
            basePropertyKey={`${fullPropertyKey}.columns`}
            columnNames={queryConfig.columnNames}
          />
        </Box>
      ) : null}

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <AthenaQueryExecuteModal
          organizationId={organizationId}
          wipConfig={wipConfig}
          athenaQuery={queryConfig}
          fullPropertyKey={fullPropertyKey}
          inputDateType="reportingDate"
          presetVariables={presetVariables}
          isOpen={isExecuteModalOpen}
          onCancel={() => setIsExecuteModalOpen(false)}
          onQuerySuccess={setQueryDataItems}
        />
      )}

      <AthenaDataPreviewModal
        queryDataItems={queryDataItems}
        onClose={() => setQueryDataItems([])}
      />
    </Flex>
  );
};

type FileInsertAthenaQueriesViewProps = {
  organizationId: string;
  fileInsertAthenaQueries: FileInsertAthenaQuery[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  presetVariables?: CreditAgreement.Config.QueryVariable[];
  validationVariables?: CreditAgreement.Config.QueryVariable[];
};

export const FileInsertAthenaQueriesView = ({
  organizationId,
  fileInsertAthenaQueries,
  wipConfig,
  basePropertyKey,
  presetVariables = [],
  validationVariables = reportingValidate.util.SUPPLEMENTAL_QUERY_VARIABLES,
}: FileInsertAthenaQueriesViewProps) => {
  const addQuery = (wc: CreditAgreementFullArtifactWipConfigType): void => {
    const queries = getIn(wc.formProps.values, basePropertyKey) ?? [];
    finleyAssert(
      Array.isArray(queries),
      `Retrieved non-array structure for [key:${basePropertyKey}]`,
    );

    const queriesCopy = lodash.cloneDeep(queries);
    queriesCopy.push(createFileInsertAthenaQueryShell(queriesCopy.length));
    wc.formProps.setFieldValue(basePropertyKey, queriesCopy);
  };

  return (
    <Box mb="6" mt="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          File Insert Athena Queries (for XLSX)
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add file insert query"
            label="Add File Insert Query"
            onClick={() => addQuery(wipConfig)}
          />
        )}
      </Flex>
      {(!fileInsertAthenaQueries || fileInsertAthenaQueries.length === 0) && (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no file insert athena queries.
        </Text>
      )}
      {fileInsertAthenaQueries?.map((query, idx) => (
        <FileInsertAthenaQueryView
          organizationId={organizationId}
          key={`file-insert-athena-query-view-${query.query.name}}`}
          wipConfig={wipConfig}
          basePropertyKey={basePropertyKey}
          fullPropertyKey={`${basePropertyKey}[${idx}]`}
          queryConfig={query}
          existingQueries={fileInsertAthenaQueries}
          idx={idx}
          presetVariables={presetVariables}
          validationVariables={validationVariables}
        />
      ))}
    </Box>
  );
};
